<script setup>
// The one public footer (2026-09-07): brand column + tagline, Explore /
// Write to us / Company, legal line. Used by the main site and by the
// partners, invest and painting subdomains, so every customer-facing host
// ends the same way. Links are absolute into the right public host and keep
// the visitor's locale prefix; the /_p dev preview collapses to its prefix.
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { platformLabel } from '@/lib/platformLabel'
import BrandFooter from '@/Components/Public/BrandFooter.vue'
import BrandLockup from '@/Components/UI/BrandLockup.vue'
import HeaderMenu from '@/Components/Public/HeaderMenu.vue'
import PlatformIcon from '@/Components/Public/PlatformIcon.vue'
import PaymentMethods from '@/Components/Public/PaymentMethods.vue'
import { currencySymbol } from '@/lib/currencySymbols'

const props = defineProps({
  hosts:        { type: Object, default: () => ({}) },  // { apex, villas, rentals, transfers, experiences } — PublicHosts.bases
  channels:     { type: Object, default: () => ({}) },  // BrandChannels.public_links (grouped by type)
  investUrl:    { type: String, default: 'https://invest.getphuket.com' },
  paintingUrl:  { type: String, default: 'https://painting.getphuket.com' },
  partnersBase: { type: String, default: 'https://partners.getphuket.com' },
  localePrefix: { type: String, default: '' },          // '' | '/ru' | '/zh' | '/th'
  homeHref:     { type: String, default: '' },
  brandName:    { type: String, default: 'Get Phuket' },
  // Bottom utility row (2026-09-07): language + currency pickers and the
  // social glyphs, the way the big marketplaces end a page. The layout owns
  // the actual switch (locale paths and the currency cookie differ per host).
  locale:        { type: String, default: '' },
  localeOptions: { type: Array,  default: () => [] },   // [{ value, label, sub }]
  currency:      { type: String, default: '' },
  currencies:    { type: Array,  default: () => [] },   // codes; hidden unless > 1
})
const emit = defineEmits(['select-lang', 'select-currency'])

const symbol = currencySymbol
const currentLocaleLabel = computed(() => (props.localeOptions.find((o) => o.value === props.locale) || {}).label || String(props.locale).toUpperCase())

const { t } = useI18n()

const COLLECTIONS = ['diving', 'watersports', 'eco', 'culture']
const collections = computed(() => COLLECTIONS.map((slug) => ({ slug, label: t(`public.nav.experience_collections.${slug}`) })))

// Absolute link into a public host, locale prefix preserved.
function on(kind, path = '/') {
  const base = props.hosts?.[kind] || ''
  if (!base) return path
  if (base.startsWith('/')) return `${base}${path === '/' ? '' : path}`
  return `${base}${props.localePrefix}${path === '/' ? '' : path}` || '/'
}
const apex = (path) => on('apex', path)
const home = computed(() => props.homeHref || apex('/'))

// "Write to us" = every channel with its glyph: chat first (WhatsApp,
// Telegram …), then the social pages (Instagram, Facebook, VK …), one row
// per platform.
// Every channel shows its platform name; the address itself sits in the link
// (and the tooltip). The phone is the exception: people look for the number,
// so the row prints it (the channel's label, else the tel: target).
const phoneNumber  = (c) => (c.label || String(c.url || '').replace(/^tel:/i, '')).trim()
const channelLabel = (c) => (c.platform === 'phone' ? phoneNumber(c) : platformLabel(c.platform))
const channelTitle = (c) => {
  if (c.platform === 'email') return String(c.url || '').replace(/^mailto:/i, '')
  if (c.platform === 'phone') return platformLabel('phone')
  return null
}
// tel: and mailto: open the phone or the mail app, never a tab.
const channelTarget = (c) => (['email', 'phone'].includes(c.platform) ? null : '_blank')
const phoneFirst = (list) => [ ...list.filter((ch) => ch.platform === 'phone'), ...list.filter((ch) => ch.platform !== 'phone') ]
// Two columns since 2026-09-08: "Write to us" is only the ways to reach a
// person (messengers, email, the concierge form); the profiles live under
// "Follow us" so the contact list stays short.
const dedupe = (list) => {
  const seen = new Set()
  return list.filter((ch) => {
    const key = String(ch.platform || ch.url || '').toLowerCase()
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}
const writeChannels  = computed(() => { const c = props.channels || {}; return phoneFirst(dedupe([ ...(c.message || []), ...(c.support || []) ])) })
// A platform that already has a chat entry (Telegram: the account under
// "Write to us" is where people should message) is not repeated as a profile.
const socialChannels = computed(() => {
  const c = props.channels || {}
  const chatting = new Set(writeChannels.value.map((ch) => String(ch.platform || '').toLowerCase()))
  return dedupe([ ...(c.social_page || []), ...(c.website || []) ]).filter((ch) => !chatting.has(String(ch.platform || '').toLowerCase()))
})
</script>

<template>
  <BrandFooter>
    <template #brand>
      <div class="pfoot__brand">
        <a :href="home" class="pfoot__lockup" :aria-label="brandName"><BrandLockup /></a>
        <p class="pfoot__tag">{{ t('public.footer.tagline') }}</p>
      </div>
    </template>
    <div class="pfoot">
      <div class="pfoot__cols">
        <div class="pfoot__col">
          <p class="pfoot__h">{{ t('public.footer.groups.explore') }}</p>
          <a :href="apex('/charters')" class="pfoot__link">{{ t('public.nav.charters') }}</a>
          <a :href="on('villas')" class="pfoot__link">{{ t('public.nav.villas') }}</a>
          <a :href="on('transfers')" class="pfoot__link">{{ t('public.nav.transfers') }}</a>
          <a :href="apex('/occasions')" class="pfoot__link">{{ t('public.occasions.eyebrow') }}</a>
          <a :href="apex('/destinations')" class="pfoot__link">{{ t('public.nav.destinations') }}</a>
          <a :href="apex('/vip')" class="pfoot__link pfoot__link--vip">{{ t('public.vip.nav') }}</a>
        </div>
        <div class="pfoot__col">
          <p class="pfoot__h">{{ t('public.nav.tours') }}</p>
          <a :href="on('experiences')" class="pfoot__link">{{ t('public.charter_home.experiences.all') }}</a>
          <a v-for="col in collections" :key="col.slug" :href="on('experiences', `/experiences/${col.slug}`)" class="pfoot__link">{{ col.label }}</a>
        </div>
        <div class="pfoot__col">
          <p class="pfoot__h">{{ t('public.footer.groups.write') }}</p>
          <a v-for="c in writeChannels" :key="c.id" :href="c.url" :target="channelTarget(c)" rel="noopener" :title="channelTitle(c)" class="pfoot__link pfoot__link--icon"><PlatformIcon :platform="c.platform" size="1rem" /><span>{{ channelLabel(c) }}</span></a>
          <a :href="apex('/plan')" class="pfoot__link pfoot__link--icon"><PlatformIcon platform="concierge" size="1rem" /><span>{{ t('public.nav.concierge') }}</span></a>
        </div>
        <div v-if="socialChannels.length" class="pfoot__col">
          <p class="pfoot__h">{{ t('public.footer.groups.social') }}</p>
          <a v-for="c in socialChannels" :key="c.id" :href="c.url" target="_blank" rel="noopener" class="pfoot__link pfoot__link--icon"><PlatformIcon :platform="c.platform" size="1rem" /><span>{{ channelLabel(c) }}</span></a>
        </div>
        <div class="pfoot__col">
          <p class="pfoot__h">{{ t('public.footer.groups.company') }}</p>
          <a :href="investUrl" class="pfoot__link">{{ t('public.nav.invest') }}</a>
          <a :href="partnersBase" class="pfoot__link">{{ t('public.footer.list_your_business') }}</a>
          <a :href="on('rentals')" class="pfoot__link">{{ t('public.nav.rentals') }}</a>
          <a :href="paintingUrl" class="pfoot__link">{{ t('public.nav.painting') }}</a>
        </div>
        <div class="pfoot__utils">
          <HeaderMenu v-if="localeOptions.length > 1" :aria-label="t('public.menu.language')" wide class="pfoot__menu">
            <template #icon>
              <PlatformIcon platform="globe" size="1rem" />
              <span class="pfoot__util-label">{{ currentLocaleLabel }}</span>
            </template>
            <template #default="{ close }">
              <button v-for="opt in localeOptions" :key="opt.value" type="button" class="hmenu__item" @click="emit('select-lang', opt.value); close()">
                <span>{{ opt.label }} <span class="hmenu__item-sub">({{ opt.sub }})</span></span>
                <svg v-if="opt.value === locale" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" class="hmenu__check" aria-hidden="true"><polyline points="5 12 10 17 19 7"/></svg>
              </button>
            </template>
          </HeaderMenu>
          <HeaderMenu v-if="currencies.length > 1" :aria-label="t('public.menu.currency')" wide class="pfoot__menu">
            <template #icon>
              <span class="pfoot__sym">{{ symbol(currency) }}</span>
              <span class="pfoot__util-label">{{ currency }}</span>
            </template>
            <template #default="{ close }">
              <button v-for="code in currencies" :key="code" type="button" class="hmenu__item" @click="emit('select-currency', code); close()">
                <span><span class="pfoot__sym">{{ symbol(code) }}</span> {{ code }}</span>
                <svg v-if="code === currency" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" class="hmenu__check" aria-hidden="true"><polyline points="5 12 10 17 19 7"/></svg>
              </button>
            </template>
          </HeaderMenu>
        </div>
      </div>
      <div class="pfoot__accept"><span class="pfoot__accept-label">{{ t('public.payments.title') }}</span><PaymentMethods compact /></div>
      <div class="pfoot__legal">
        <span>© {{ new Date().getFullYear() }} Get Phuket Co., Ltd. · Reg. 0835557007124</span>
        <span class="pfoot__dot">·</span>
        <a :href="apex('/legal/terms')" class="pfoot__link">{{ t('public.legal.terms') }}</a>
        <span class="pfoot__dot">·</span>
        <a :href="apex('/legal/refunds')" class="pfoot__link">{{ t('public.legal.refunds') }}</a>
        <span class="pfoot__dot">·</span>
        <a :href="apex('/legal/privacy')" class="pfoot__link">{{ t('public.legal.privacy') }}</a>
        <span class="pfoot__dot">·</span>
        <a :href="apex('/legal/company')" class="pfoot__link">{{ t('public.legal.company') }}</a>
      </div>
    </div>
  </BrandFooter>
</template>

<style scoped>
:deep(.brand-footer) { padding-top: clamp(2rem, 4vw, 3rem); }
:deep(.brand-footer__inner) { align-items: flex-start; gap: 2.5rem clamp(2.5rem, 6vw, 6rem); }
:deep(.brand-footer__content) { flex: 1 1 32rem; }
.pfoot__brand { display: grid; gap: 0.9rem; max-width: 17rem; padding-top: 0.15rem; }
.pfoot__lockup { display: inline-flex; text-decoration: none; color: var(--ink); }
.pfoot__tag { margin: 0; font-family: var(--display); font-style: italic; font-size: 1.05rem; line-height: 1.35; color: var(--inkSoft); }
/* The shell centres its text under 540px; the link columns stay left-aligned regardless. */
.pfoot { width: 100%; display: grid; gap: 1.75rem; text-align: left; }
.pfoot__col .pfoot__link { text-align: left; }
/* Five link columns + the language/currency cell on the far right, level with the column heads. */
.pfoot__cols { display: grid; grid-template-columns: repeat(5, minmax(0, 10rem)) minmax(0, 1fr); gap: 1.5rem 1.5rem; }
.pfoot__col { display: grid; gap: 0.45rem; align-content: start; justify-items: start; }
.pfoot__h { margin: 0 0 0.35rem; font-size: 0.66rem; letter-spacing: var(--eyebrow-tracking, 0.18em); text-transform: uppercase; font-weight: 600; color: color-mix(in oklch, var(--gold, var(--warn)) 62%, var(--ink)); }
.pfoot__link { color: var(--muted); text-decoration: none; }
.pfoot__link:hover { color: var(--ink); }
.pfoot__col .pfoot__link { font-size: 0.9rem; color: var(--inkSoft); }
.pfoot__col .pfoot__link:hover { color: var(--ink); }
.pfoot__link--vip { color: color-mix(in oklch, var(--gold, var(--warn)) 62%, var(--ink)); font-weight: 600; }
.pfoot__dot { opacity: 0.5; }
.pfoot__link--icon { display: inline-flex; align-items: center; gap: 0.5rem; }
.pfoot__accept { display: flex; flex-wrap: wrap; align-items: center; gap: 0.5rem 1rem; padding-top: 1rem; border-top: 1px solid var(--line); }
.pfoot__accept-label { font-size: 0.66rem; letter-spacing: var(--eyebrow-tracking, 0.18em); text-transform: uppercase; font-weight: 600; color: color-mix(in oklch, var(--gold, var(--warn)) 62%, var(--ink)); }
.pfoot__legal { display: flex; flex-wrap: wrap; gap: 0.4rem 0.75rem; align-items: center; padding-top: 0.9rem; border-top: 1px solid var(--line); font-size: 0.78rem; color: var(--muted); }
/* Utility row: language, currency, social glyphs — one quiet family of marks. */
.pfoot__utils { display: flex; flex-wrap: wrap; align-items: center; justify-content: flex-end; align-self: start; gap: 0.25rem 0.5rem; margin: -0.45rem -0.5rem 0 0; }
/* Dropdown items live in HeaderMenu's slot — the header styles them under its own scope, so the footer does the same. */
.pfoot__menu :deep(.hmenu__item) { display: flex; align-items: center; justify-content: space-between; gap: 0.75rem; width: 100%; padding: 0.55rem 0.75rem; background: transparent; border: 0; border-radius: 0.4rem; color: var(--ink); font: inherit; font-size: 0.875rem; font-weight: 500; text-align: left; cursor: pointer; white-space: nowrap; transition: background 0.1s; }
.pfoot__menu :deep(.hmenu__item:hover) { background: color-mix(in oklch, var(--ink) 6%, transparent); }
.pfoot__menu :deep(.hmenu__item-sub) { color: var(--muted); font-size: 0.8125rem; margin-left: 0.125rem; }
.pfoot__menu :deep(.hmenu__check) { width: 1rem; height: 1rem; color: var(--accent); flex-shrink: 0; }
.pfoot__menu :deep(.hmenu__btn) { color: var(--ink); font-size: 0.82rem; font-weight: 600; font-family: var(--font-body, var(--body)); }
.pfoot__util-label { white-space: nowrap; }
.pfoot__sym { display: inline-block; min-width: 1rem; text-align: center; font-family: var(--font-mono, var(--mono)); font-weight: 600; }
@media (max-width: 56rem) { .pfoot__cols { grid-template-columns: 1fr 1fr; } .pfoot__utils { grid-column: 1 / -1; justify-content: flex-start; margin: 0 0 0 -0.5rem; } }
</style>
